/**
 * VideoHandler
 * Class for video operations under Chrome, although curiously there is code for browser compatability.
 * Class only used by ChromeWrapper/ChromeProxy, so not sure why the browser compatibility checks
 */
namespace SmartVideo {

interface IVidSize {
        h: number
        w: number
}

export class VideoHandler {
    origCallback: Function;
    streamCount: number;
    streamsChecked: number;
    callbackCalled: boolean;
    inited: boolean;
    userMediaStream: MediaStream;
    video: HTMLVideoElement;
    vidSize: IVidSize;
    intervalFunction: number;
    cameraCheckTimeout: number;
    usermediaMessage: HTMLElement;
    sendEvent: Function;
    sendRequest: Function;

    constructor(sendEvent: Function) {
        this.origCallback = null;
        this.streamCount = 0;
        this.streamsChecked = 0;
        this.callbackCalled = false;
        this.inited = false;
        this.userMediaStream = null;
        this.vidSize = { w: 0, h: 0 };
        this.cameraCheckTimeout = null;
        this.video = null;
        this.sendEvent = sendEvent;
        this.sendRequest = sendEvent;

        this.intervalFunction = setInterval(() => {
            if (this.video) {
                // Check Video size
                if (this.vidSize.w !== this.video.offsetWidth || this.vidSize.h !== this.video.offsetHeight) {
                    this.vidSize.w = this.video.offsetWidth;
                    this.vidSize.h = this.video.offsetHeight;
                    console.log('video resized to', this.video.offsetWidth, this.video.offsetHeight);
                    sendEvent({type: 'InEventResize', x: 0, y: 0, width: this.video.offsetWidth, height: this.video.offsetHeight},
                     request => {
                        console.log(request);
                    });
                }
            }
        }, 200);
    }

    start(videoElement, callback) {
        if (!this.cameraCheckTimeout) {
            this.cameraCheckTimeout = setTimeout(() => {
                this.cameraCheckTimeout = null;
            }, 45 * 1000);
        }

        this.video = videoElement;
        if (!this.video) {
            console.log('Init the videoHandler before calling this method');
            return;
        }
        if (!this.origCallback) {
            this.origCallback = callback;
        }

        this.usermediaMessage = document.getElementById('lbl_usermedia');
        this.video.onloadedmetadata = this.displayVideoDimensions.bind(this);

        if (MediaStreamTrack) {
            this.getMediaStreamTrackSource();
        } else {
            this.tempCallback('This browser does not support MediaStreamTrack', null);
        }
    }

    stop() {
        if (!this.video) {
            console.log('Init the videoHandler before calling this method');
            return;
        }
        this.video.pause();
        this.video.src = '';
        if (this.userMediaStream) {
            this.userMediaStream.getTracks()[0].stop();
        }
    }

    tempCallback(message, permissionDeniedError) {
        if (!this.callbackCalled) {
            this.callbackCalled = true;

        }
    }

    displayVideoDimensions() {
        if (!this.video.videoWidth || this.video.videoWidth < 100) {
            setTimeout(this.displayVideoDimensions.bind(this), 500);
            return;
        }
        if (navigator.appVersion.indexOf('Windows') !== -1 || navigator.appVersion.indexOf('SmartVideoAgent') !== -1) {
            this.sendRequest({type: 'RequestSetCameraResolution', width: this.video.videoWidth, height: this.video.videoHeight});
            console.log('VideoHandler', 'Windows Video Width: ', this.video.videoWidth, ' Video Height: ', this.video.videoHeight);
        } else {
            // this.sendRequest({type: 'RequestSetCameraResolution', width: 1920, height: 1080});
            console.log('VideoHandler', 'OSX forcing Video Width: 1920 Video Height: 1080');
        }
    }

    getMediaStreamTrackSource() {
        const that = this;
        console.log('VideoHandler.getMediaStreamTrackSource called');
        if (navigator.mediaDevices || navigator.mediaDevices.enumerateDevices) {
            navigator.mediaDevices.enumerateDevices().then(function(devices){
                let constraints;
                console.log('getMediaStreamTrackSource: Found ' + devices.length + ' devices.');
                for (let index = 0; index < devices.length; index++) {
                    const device = devices[index];
                    if (device.kind === 'videoinput') {
                            if (navigator.appVersion.indexOf('Windows') !== -1 || navigator.appVersion.indexOf('SmartVideoAgent') !== -1) {
                                constraints = {
                                    audio: false,
                                    video: {
                                        optional: [
                                            {sourceId: device.deviceId},
                                            {minWidth: 1920},
                                            {minHeight: 1080}
                                        ]
                                    }
                                };
                            } else {
                            // For OSX
                                constraints = {
                                    audio: false,
                                    video: {
                                        optional: [
                                            {sourceId: device.deviceId}
                                        ]
                                    }
                                };
                            }
                    }
                }
                navigator.mediaDevices.getUserMedia(constraints)
                .then(that.onStreamSuccess.bind(that))
                .catch(that.onStreamErrors.bind(that));
            });
            return;
        }
    }

    onStreamSuccess(stream) {
        const track = stream.getVideoTracks()[0];
        console.log('VideoHandler', 'Source id: ' + track.id);
        console.log('VideoHandler', 'Source label: ' + track.label);

        if (track.label.indexOf('WebPluginVirtualCamera') >= 0) {
            console.log('WebPluginVirtualCamera found');
            // Play the media stream in the video element
            this.userMediaStream = stream;
            this.video.src = window.URL.createObjectURL(stream);
            console.log('WebPluginVirtualCamera stream added as video source');
            this.video.play();
            this.tempCallback(null, false);
            clearTimeout(this.cameraCheckTimeout);
            this.cameraCheckTimeout = null;
        } else {
            stream.getTracks()[0].stop();
            this.streamsChecked++;
            if (this.streamsChecked >= this.streamCount) {
                console.log('VideoHandler', 'WebPluginVirtualCamera not found. Cameradriver is not installed or not recognized');
                setTimeout(() => {
                    console.log('Reattempting GetUserMedia');
                    if (this.cameraCheckTimeout) {
                        this.start(this.video, this.origCallback.bind(this));
                    } else {
                        this.tempCallback('cameraTimeout', false);
                    }
                }, 1000);
            }
        }
    }

    onStreamErrors(error) {
        this.streamsChecked++;
        if (this.streamsChecked >= this.streamCount) {
            // Handle the permission denied error in Chrome and 'Starting video failed' error in Firefox
            if (error.name === 'PermissionDeniedError' || error === 'Starting video failed') {
                this.tempCallback('Media might already be in use', true);
                console.log('VideoHandler', 'Media might already be in use');
            } else { // Handle all other errors
                this.tempCallback('Something went wrong while getting the video', false);
                console.log('VideoHandler', 'Error: ', JSON.stringify(error));
            }
        }
    }
}}
